import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Users, DollarSign, Check, Plus, Minus, MapPin, Calendar, Building2, Loader2, Star } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import ConsumerLayout from '@/components/ConsumerLayout';
import { venueUrl } from '@/lib/urls';
import api from '@/lib/api';
import { toast } from 'sonner';

const EVENT_TYPE_LABELS = {
  christmas: 'Christmas Party', awards: 'Awards Ceremony', launch: 'Launch Party',
  baby_shower: 'Baby Shower', birthday: 'Birthday Party', wedding: 'Wedding Reception',
  general: 'Private Event', corporate_general: 'Corporate Event',
  conference: 'Conference', holiday: 'Holiday Party', team_building: 'Team Building',
};

const CATEGORY_COLORS = { corporate: 'bg-blue-100 text-blue-700', social: 'bg-pink-100 text-pink-700' };

export default function PrivateHireDetail() {
  const { packageId } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guestCount, setGuestCount] = useState(20);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    api.get(`/private-hire/package/${packageId}`).then(res => { setPkg(res.data); setGuestCount(res.data.min_guests || 20); }).catch(() => toast.error('Package not found')).finally(() => setLoading(false));
  }, [packageId]);

  useEffect(() => {
    if (!pkg) return;
    const timer = setTimeout(() => calculatePrice(), 300);
    return () => clearTimeout(timer);
  }, [guestCount, selectedAddons, pkg]);

  const calculatePrice = async () => {
    if (!pkg || guestCount < (pkg.min_guests || 1)) return;
    setCalculating(true);
    try {
      const res = await api.post('/private-hire/calculate-price', { package_id: pkg.id, guest_count: guestCount, addons: selectedAddons });
      setPricing(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Pricing error'); }
    finally { setCalculating(false); }
  };

  const toggleAddon = (name) => setSelectedAddons(prev => prev.includes(name) ? prev.filter(a => a !== name) : [...prev, name]);

  if (loading) return <ConsumerLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div></ConsumerLayout>;
  if (!pkg) return <ConsumerLayout><div className="text-center py-20"><p>Package not found</p></div></ConsumerLayout>;

  const space = pkg.space;
  const venue = pkg.venue;

  return (
    <ConsumerLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Link to={venue ? venueUrl(venue) : '/venues'} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to {venue?.name || 'Venue'}
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left — Package Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Hero */}
            {(pkg.images?.length > 0 || space?.images?.length > 0) && (
              <div className="aspect-video rounded-xl overflow-hidden">
                <img src={pkg.images?.[0] || space?.images?.[0]} alt={pkg.name} className="w-full h-full object-cover" />
              </div>
            )}

            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge className={CATEGORY_COLORS[pkg.category] || 'bg-gray-100 text-gray-700'}>{pkg.category === 'corporate' ? 'Corporate' : 'Social'}</Badge>
                <Badge variant="outline">{EVENT_TYPE_LABELS[pkg.event_type] || pkg.event_type}</Badge>
              </div>
              <h1 className="text-3xl font-bold">{pkg.name}</h1>
              {venue && <p className="text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-4 h-4" /> {venue.name} — {venue.address}</p>}
            </div>

            <p className="text-muted-foreground leading-relaxed">{pkg.description}</p>

            {/* Space Info */}
            {space && (
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-indigo-600" /> {space.name}</CardTitle></CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-sm mb-3">{space.description}</p>
                  <div className="flex gap-4 text-sm">
                    <span className="flex items-center gap-1"><Users className="w-4 h-4 text-muted-foreground" /> {space.capacity_min}–{space.capacity_max} guests</span>
                    {space.size_sqft && <span>{space.size_sqft} sq ft</span>}
                  </div>
                  {space.features?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">{space.features.map(f => <Badge key={f} variant="secondary" className="text-xs">{f}</Badge>)}</div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* What's Included */}
            {pkg.includes?.length > 0 && (
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-lg">What's Included</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {pkg.includes.map((item, i) => (
                      <div key={i} className="flex items-start gap-2"><Check className="w-5 h-5 text-green-500 mt-0.5 shrink-0" /><span className="text-sm">{item}</span></div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Add-ons */}
            {pkg.addons?.length > 0 && (
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-lg">Optional Add-ons</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                  {pkg.addons.map((addon, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border rounded-lg hover:border-indigo-300 cursor-pointer" onClick={() => toggleAddon(addon.name)}>
                      <div className="flex items-center gap-3">
                        <Checkbox checked={selectedAddons.includes(addon.name)} />
                        <div>
                          <p className="font-medium text-sm">{addon.name}</p>
                          {addon.description && <p className="text-xs text-muted-foreground">{addon.description}</p>}
                        </div>
                      </div>
                      <span className="font-semibold text-sm">+${addon.price?.toLocaleString()}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right — Pricing Calculator */}
          <div>
            <Card className="sticky top-8" data-testid="pricing-calculator">
              <CardHeader className="pb-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
                <CardTitle className="text-lg">Instant Pricing</CardTitle>
                <p className="text-sm text-white/80">{pkg.duration_label || `${pkg.duration_hours} hours`}</p>
              </CardHeader>
              <CardContent className="p-5 space-y-5">
                {/* Guest Count */}
                <div>
                  <Label className="mb-2 block">Number of Guests</Label>
                  <div className="flex items-center gap-3">
                    <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => setGuestCount(Math.max(pkg.min_guests || 1, guestCount - 5))}><Minus className="w-4 h-4" /></Button>
                    <Input type="number" value={guestCount} onChange={e => setGuestCount(Math.max(1, parseInt(e.target.value) || 0))} className="text-center text-lg font-bold h-10" />
                    <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => setGuestCount(Math.min(pkg.max_guests || 500, guestCount + 5))}><Plus className="w-4 h-4" /></Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{pkg.min_guests}–{pkg.max_guests} guests for this space</p>
                </div>

                {/* Price Breakdown */}
                {pricing && (
                  <div className="space-y-2 text-sm border-t pt-4">
                    <div className="flex justify-between"><span className="text-muted-foreground">Venue rental</span><span>${pricing.base_price?.toLocaleString()}</span></div>
                    {pricing.per_person_price > 0 && <div className="flex justify-between"><span className="text-muted-foreground">{pricing.guest_count} guests × ${pricing.per_person_price}</span><span>${pricing.per_person_total?.toLocaleString()}</span></div>}
                    {pricing.addon_breakdown?.map((a, i) => <div key={i} className="flex justify-between"><span className="text-muted-foreground">{a.name}</span><span>+${a.price?.toLocaleString()}</span></div>)}
                    {pricing.min_spend_applies && <div className="flex justify-between text-amber-600"><span>Minimum spend applies</span><span>${pricing.min_spend?.toLocaleString()}</span></div>}
                    <div className="flex justify-between font-bold text-lg border-t pt-3">
                      <span>Total</span>
                      <span className="text-indigo-600">${pricing.total?.toLocaleString()}</span>
                    </div>
                  </div>
                )}

                {calculating && <div className="text-center py-2"><Loader2 className="w-5 h-5 animate-spin mx-auto text-indigo-600" /></div>}

                <Button className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 font-semibold" onClick={() => toast.success('Inquiry sent! The venue will contact you within 24 hours.')}>
                  Request to Book
                </Button>
                <p className="text-[11px] text-center text-muted-foreground">Final pricing confirmed by venue. No charge until booking is confirmed.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ConsumerLayout>
  );
}
